import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
  AxiosError,
} from "axios";

import LogoutTimer from "@/usecases/LogoutTimer";
import TokenUseCase from "@/usecases/TokenUseCase";
import { GENERAL_HEADER } from "@/shared/constants";
import {
  createApiException,
  ExceptionType,
  WebApiMessageSeverity,
} from "../ResponseModel";

export abstract class HttpClient {
  protected readonly httpClient: AxiosInstance;
  readonly _Bearer = "Bearer ";

  public constructor(
    baseURL: string,
    withCredentials = true,
    headers: { "Content-Type"?: string; Accept?: string } = GENERAL_HEADER,
  ) {
    this.httpClient = axios.create({
      baseURL,
      withCredentials,
      headers,
    });

    /* axiosRetry(
        this.httpClient, 
        {
          retries:1, 
          retryDelay: axiosRetry.exponentialDelay
        }
      ) */

    this._initializeInterceptor();
  }

  private _initializeInterceptor = () => {
    this.httpClient.interceptors.response.use(
      this._handleResponse,
      this._handleResponseError,
    );

    this.httpClient.interceptors.request.use(
      // Mismatch for function type, I think it can be ignored
      // @ts-ignore
      this._handleRequest,
      this._handleRequestError,
    );
  };

  private _handleRequest = async (
    request: AxiosRequestConfig,
  ): Promise<AxiosRequestConfig> => {
    if (!request?.headers) {
      throw new Error(
        `Expected 'request' and 'request.headers' not to be undefined`,
      );
    } else {
      if (
        typeof request.headers.Authorization !== "string" ||
        request.headers.Authorization.length == 0
      ) {
        let token = await new TokenUseCase().getAccessToken();
        if (token.length > 0)
          request.headers["Authorization"] =
            // `${this._Bearer}${this.ACCESS_TK}`;
            this._Bearer + token;
      }

      if (
        request.headers["TimezoneOffset"] === undefined ||
        request.headers["TimezoneOffset"].length == 0
      ) {
        request.headers.TimezoneOffset = new Date().getTimezoneOffset() / -60;
      }
    }

    return request;
  };

  protected _handleRequestError = (error: AxiosError): Promise<AxiosError> => {
    let err = this.getError(error);
    let message = Array.isArray(err) ? err.join(",") : err;
    // error.message = message;
    return Promise.reject({ ...err, status: error.status });
  };

  private _handleResponse = (response: AxiosResponse): AxiosResponse => {
    this.resetTimer();
    // This is the fix for logout response as it only returns data as an empty string and no status
    if (!response.data.status)
      response.data = { status: 200, content: response.data };
    return response;
  };

  protected _handleResponseError = (error: AxiosError): Promise<AxiosError> => {
    console.log("Error received => ", JSON.stringify(error));
    let err = this.getError(error);

    // error.message = message;
    console.log("Error in Client formatted: ", err);
    this.resetTimer();
    return Promise.reject(err);
  };

  private getError(error: AxiosError) {
    // To check the format of different Errors encountered
    if (error.response?.data) {
      // @ts-ignore
      if (error.response.data.errors) {
        const ex = error.response.data;

        return createApiException(
          // @ts-ignore
          ex.title,
          "",
          "",
          // @ts-ignore
          ex.status,
          // @ts-ignore
          this.objectToArrayString(ex.errors),
          ExceptionType.Business,
          WebApiMessageSeverity.Error,
        );
      }
      return error.response.data;
    }
    //return error;
    if (error.message)
      return createApiException(
        error.message,
        "",
        "",
        error.status ?? 503,
        [],
        ExceptionType.System,
        WebApiMessageSeverity.Error,
      );

    // TODO: Remove after we don't need to check the error formats
    if (error.response == null) return error.message;
    else {
      //@ts-ignore
      if (error.response.data.data == null) {
        // @ts-ignore
        if (error.response.data.content || error.response.data.messages)
          // @ts-ignore
          return error.response.data;
        // @ts-ignore
        //if (error.response.data.messages) return error.response.data.messages;
        // @ts-ignore
        if (error.response.data.message) return error.response.data.message;
        //@ts-ignore
        else if (error.response.data.errors && error.message) return error;
        //@ts-ignore
        else if (error.response.data.error == null) return error.message;
        //@ts-ignore
        else return error.response.data.errors;
      }
      //@ts-ignore
      else return error.response.data.data;
    }
  }

  private async resetTimer() {
    // const logoutTime = await new TokenUseCase().getTokenExpiryTime();

    // if (logoutTime) {
    //   let timer = new LogoutTimer();
    //   /**
    //    * logoutTime is in UTC second
    //    * So we subtract the current UTC time in seconds
    //    * This gives the remaining time in seconds
    //    * Floor to get rid of decimal places. Logout early than late.
    //    */
    //   const logoutCountDown = Math.floor(logoutTime - Date.now() / 1000);
    //   if (logoutCountDown > 0) timer.resetTimer();
    //   else await new TokenUseCase().clearAccessToken();
    // }
    const timer = new LogoutTimer();

    timer.updateToken();
  }

  private objectToArrayString(obj: any): string[] {
    if (obj == null) return [];
    const result: string[] = [];
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = Array.isArray(obj[key]) ? obj[key].join(", ") : obj[key];
        result.push(`${key}: ${value}`);
      }
    }
    return result;
  }
}
